import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { ComboBox, Text } from '@reveni-io/reveni-ui'
import { Divider } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { availableLanguagesForUser } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useDragSort from '@/common/hooks/useDragSort/useDragSort'

import ActionsSection from './ActionsSection/ActionsSection'
import CustomMessage from './ActionsSection/CustomMessage/CustomMessage'
import AdditionalContentSection from './AdditionalContentSection/AdditionalContentSection'
import CustomTitle from './CustomTitle/CustomTitle'
import useReturnReason from './hooks/useReturnReason/useReturnReason'
import ReturnReasonDetailConfigurationSkeleton from './ReturnReasonDetailConfigurationSkeleton/ReturnReasonDetailConfigurationSkeleton'
import SubReasonsSection from './SubReasonsSection/SubReasonsSection'
import TransactionTypeSection from './TransactionTypeSection/TransactionTypeSection'
import * as Styles from './styles'

const ReturnReasonDetailConfiguration = () => {
  const { info } = useContext(AppContext)
  const { id } = useParams()
  const { t } = useTranslation()
  const location = useLocation()
  const {
    fetchingProductCategories,
    selectedType,
    setSelectedType,
    reasons,
    transactionTypes,
    setTransactionTypes,
    actionForTransactionTypes,
    setActionForTransactionTypes,
    setUseCustomMessage,
    setCustomTitles,
    setCustomMessages,
    customTitles,
    customMessages,
    useCustomMessage,
    useCustomTitle,
    setUseCustomTitle,
    notesValidation,
    onToggleNotesValidation,
    imageValidation,
    onToggleImageValidation,
    onChangeNotesValidation,
    onChangeImageValidation,
    hasChanges,
    revertChanges,
    onCreateReturnReason,
    onEditReturnReason,
    productTypes,
    selectedProductTypes,
    setSelectedProductTypes,
    error,
    subReasons,
    addSubReason,
    deleteSubReason,
    editSubReasonInternalName,
    editSubReasonDisplay,
    reorderSubReasons,
    allSubReasonsAreValid,
    productCategories,
    selectedCategories,
    setSelectedCategories,
    returnPortalFeatures,
    fetchingReturnPortalFeatures,
    action,
    onChangeAction,
    bonusTypes,
    setBonusTypes,
    tags,
    setTags,
    matchCase,
    setMatchCase,
    availabilityMode,
    setAvailabilityMode,
  } = useReturnReason({
    id,
    storeId: info?.selectedStore?.id,
    selectedReturnReason: location.state?.selectedReturnReason,
    returnPortalId: info?.selectedReturnPortal?.id,
  })
  const supportedLanguages = info?.selectedStore?.supported_languages || ['en']

  const {
    dragging: draggingSubReason,
    onSortStart: onSubReasonSortStart,
    onSortEnd: onSubReasonSortEnd,
  } = useDragSort(reorderSubReasons)

  const getAvailableLanguages = () => {
    return availableLanguagesForUser.filter(lang => supportedLanguages?.includes(lang))
  }

  const handleChangeCustomTitle = (value, lang) => {
    setCustomTitles(translations => ({ ...translations, [lang]: value }))
  }

  const handleChangeCustomMessage = (value, lang) => {
    setCustomMessages(translations => ({ ...translations, [lang]: value }))
  }

  const handleCheckUseCustomTitle = e => {
    setUseCustomTitle(e.target.checked)
  }

  const handleCheckUseCustomMessage = e => {
    setUseCustomMessage(e.target.checked)
  }

  const isFormValid = () => {
    let isValid = true

    if (useCustomTitle) {
      isValid = supportedLanguages.every(lang => customTitles?.[lang] && customTitles?.[lang]?.length <= 128)
    }

    if (useCustomMessage) {
      isValid = supportedLanguages.every(lang => customMessages?.[lang])
    }

    isValid = allSubReasonsAreValid()

    return isValid
  }

  const handleOnSave = () => {
    if (id === 'new') {
      onCreateReturnReason()
    } else {
      onEditReturnReason()
    }
  }

  if (fetchingProductCategories || fetchingReturnPortalFeatures) {
    return <ReturnReasonDetailConfigurationSkeleton />
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert />}
      <Styles.Section>
        <Text type="titleS">{t('Tipo')}</Text>
        <ComboBox
          value={selectedType}
          handleChange={e => setSelectedType(e.target.value)}
          menuItems={reasons}
          label={t('Tipo')}
          fullWidth
        />
        <CustomTitle
          languages={getAvailableLanguages()}
          onChangeCustomTitle={handleChangeCustomTitle}
          customTitles={customTitles}
          onCheckUseCustomTitle={handleCheckUseCustomTitle}
          useCustomTitle={useCustomTitle}
        />
      </Styles.Section>
      <Divider />
      <TransactionTypeSection
        transactionTypes={transactionTypes}
        onChangeTransactionTypes={setTransactionTypes}
        error={error}
        productTypes={productTypes || []}
        productCategories={productCategories}
        selectedProductTypes={selectedProductTypes}
        onChangeProductTypes={setSelectedProductTypes}
        selectedCategories={selectedCategories}
        onChangeSelectedCategories={setSelectedCategories}
        returnReasonsFiltering={returnPortalFeatures?.return_reasons_filtering}
        availabilityMode={availabilityMode}
        onChangeAvailabilityMode={setAvailabilityMode}
        tags={tags}
        onChangeTags={setTags}
        matchCase={matchCase}
        onChangeMatchCase={setMatchCase}
      />
      <Divider />
      <ActionsSection
        actionForTransactionTypes={actionForTransactionTypes}
        onChangeActionForTransactionTypes={setActionForTransactionTypes}
        setUseCustomMessage={setUseCustomMessage}
        action={action}
        onChangeAction={onChangeAction}
        bonusTypes={bonusTypes}
        onChangeBonusTypes={setBonusTypes}
      />
      <Divider />
      <CustomMessage
        languages={getAvailableLanguages()}
        useCustomMessage={useCustomMessage}
        onCheckUseCustomMessage={handleCheckUseCustomMessage}
        onChangeCustomMessage={handleChangeCustomMessage}
        customMessages={customMessages}
        action={action}
        actionForTransactionTypes={actionForTransactionTypes}
      />
      <Divider />
      <AdditionalContentSection
        notesValidation={notesValidation}
        imageValidation={imageValidation}
        onToggleNotesValidation={onToggleNotesValidation}
        onToggleImageValidation={onToggleImageValidation}
        onChangeNotesValidation={onChangeNotesValidation}
        onChangeImageValidation={onChangeImageValidation}
      />
      <Divider />
      <SubReasonsSection
        onAddSubReason={addSubReason}
        onDeleteSubReason={deleteSubReason}
        onEditSubReasonInternalName={editSubReasonInternalName}
        onEditSubReasonDisplay={editSubReasonDisplay}
        languages={getAvailableLanguages()}
        subReasons={subReasons}
        onSortEnd={onSubReasonSortEnd}
        onSortStart={onSubReasonSortStart}
        dragging={draggingSubReason}
      />
      <SaveChangesButtons
        onSave={handleOnSave}
        hasChanges={hasChanges}
        onRevertChanges={revertChanges}
        disabled={!isFormValid()}
      />
    </Styles.Container>
  )
}

export default ReturnReasonDetailConfiguration
